import { get, del } from './api';

// Industry insights API types
export interface Industry {
  name: string;
  display_name: string;
  description: string;
  icon: string | null;
  categories: string[];
}

export interface IndustriesListResponse {
  industries: Industry[];
  count: number;
}

export interface Category {
  name: string;
  display_name: string;
  description: string;
  supported_file_types: string[];
}

export interface CategoriesListResponse {
  industry: string;
  categories: Category[];
  count: number;
}

export interface DocumentMetadata {
  title?: string;
  description?: string;
  authors?: string[];
  tags?: string[];
  source_url?: string;
  publication_date?: string;
  custom_metadata?: Record<string, any>;
}

export interface Document {
  filename: string;
  original_filename: string;
  industry: string;
  category: string;
  url: string;
  size: number;
  mime_type: string;
  upload_date?: string;
  last_modified: string;
  metadata?: DocumentMetadata;
}

export interface DocumentListResponse {
  industry: string;
  category: string;
  documents: Document[];
  count: number;
}

// Industry Insights API functions
export const getIndustries = (): Promise<IndustriesListResponse> => {
  return get<IndustriesListResponse>('/api/v1/industry-insights/');
};

export const getCategories = (industry: string): Promise<CategoriesListResponse> => {
  return get<CategoriesListResponse>(`/api/v1/industry-insights/${industry}/categories`);
};

export const getDocuments = (industry: string, category: string): Promise<DocumentListResponse> => {
  return get<DocumentListResponse>(`/api/v1/industry-insights/${industry}/${category}/documents`);
};

export const getDocumentMetadata = (industry: string, category: string, filename: string): Promise<Document> => {
  return get<Document>(`/api/v1/industry-insights/${industry}/${category}/documents/${filename}`);
};

export const getDocumentUrl = (industry: string, category: string, filename: string): string => {
  // This returns the URL for downloading the document
  return `/api/v1/industry-insights/${industry}/${category}/documents/${filename}/download`;
};

export const deleteDocument = (industry: string, category: string, filename: string): Promise<{ message: string }> => {
  return del<{ message: string }>(`/api/v1/industry-insights/${industry}/${category}/documents/${filename}`);
};

// Helper function to get metadata schema to guide form creation in the UI
export const getMetadataSchema = (): Promise<any> => {
  return get<any>('/api/v1/industry-insights/metadata.json');
};

// Function to prepare FormData for document upload
export const createDocumentFormData = (
  industry: string,
  category: string,
  file: File,
  metadata?: {
    title?: string;
    description?: string;
    authors?: string[];
    tags?: string[];
    source_url?: string;
  }
): FormData => {
  const formData = new FormData();
  formData.append('industry', industry);
  formData.append('category', category);
  formData.append('file', file);

  if (metadata?.title) {
    formData.append('title', metadata.title);
  }

  if (metadata?.description) {
    formData.append('description', metadata.description);
  }

  if (metadata?.authors && metadata.authors.length > 0) {
    formData.append('authors', metadata.authors.join(','));
  }

  if (metadata?.tags && metadata.tags.length > 0) {
    formData.append('tags', metadata.tags.join(','));
  }

  if (metadata?.source_url) {
    formData.append('source_url', metadata.source_url);
  }

  return formData;
};

// Note: This function is different because it uses FormData instead of JSON
export const uploadDocument = async (formData: FormData): Promise<Document> => {
  // Get authentication token
  const token = await fetch('/api/auth/token').then(r => r.text());
  
  if (!token) {
    throw new Error('Authentication required');
  }

  // Create URL - allow for standalone mode in the future
  const apiBaseUrl = process.env.NODE_ENV === 'development' 
    ? '' // Use relative URL in development
    : (process.env.REACT_APP_INDUSTRY_INSIGHTS_API_URL || process.env.REACT_APP_API_URL);
  
  const url = `${apiBaseUrl}/api/v1/industry-insights/upload`;
  
  // Upload the document
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    body: formData
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error uploading document');
  }

  return await response.json();
};
